import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { TONE_CLASS, TONE_VAR } from '../lib/status.ts';
import type { Tone } from '../lib/status.ts';
import { badgeVariants } from '../primitives/badge.tsx';
import { StatusDot } from './StatusDot.tsx';

export interface BadgeProps extends Omit<ComponentProps<'span'>, 'children'> {
  tone?: Tone;
  /** soft: tinted fill (default). outline: border only. solid: strong fill for counts. */
  variant?: 'soft' | 'outline' | 'solid';
  size?: 'sm' | 'md';
  /** Leading status dot (pulses for live tones). */
  dot?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

/**
 * Text in a tone's colour on its own tint: the tone pulled toward the theme's text colour. The
 * raw tone (a status or the accent) is made for dots and fills, and as small text on its tint
 * it reads under 4.5:1 in several themes (the accent at 3.3:1, ended at 3.2:1 in dark).
 */
export function toneInk(tone: Tone): string {
  // The accent has its own text colour per theme.
  if (tone === 'accent') return 'var(--accent-text)';
  return `color-mix(in oklab, ${TONE_VAR[tone]} 62%, var(--fg))`;
}

/**
 * A compact label: a tag, a count, a kind. On the shadcn Badge (20 px, 12 px text, neutral by
 * default). Text must be a human label, never a raw enum value. A status in a row is a
 * StatusPill, not a coloured badge. Toned text is inked (toneInk) so it reads at 4.5:1.
 */
export function Badge({ tone = 'neutral', variant = 'soft', size = 'sm', dot, icon, className, children, style, ...rest }: BadgeProps) {
  const c = TONE_CLASS[tone];
  const inked = variant !== 'solid' && tone !== 'neutral';
  return (
    <span
      data-slot="badge"
      data-chip=""
      style={inked ? { color: toneInk(tone), ...style } : style}
      className={cn(
        badgeVariants({ variant: variant === 'outline' ? 'outline' : 'default' }),
        'num',
        size === 'md' && 'h-(--pill-height-md) gap-1.5',
        variant === 'soft' && tone !== 'neutral' && cn(c.soft, c.fg),
        variant === 'outline' && tone !== 'neutral' && c.fg,
        variant === 'solid' && cn(c.bg, tone === 'accent' ? 'text-accent-fg' : 'text-bg'),
        className,
      )}
      {...rest}
    >
      {dot && <StatusDot tone={tone} size="xs" label="" />}
      {icon}
      {children != null && <span className="truncate">{children}</span>}
    </span>
  );
}
